import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface GoogleImagen4UltraGenerateParams {
  /**
   * 图像生成提示词
   */
  prompt: string;
  /**
   * 负面提示词，描述不希望在图像中出现的内容
   */
  negative_prompt?: string;
  /**
   * 图像宽高比
   * @default "1:1"
   * @values 1:1 | 9:16 | 16:9 | 3:4 | 4:3
   */
  aspect_ratio?: '1:1' | '9:16' | '16:9' | '3:4' | '4:3';
  /**
   * 人物生成控制
   * @default "dont_allow"
   * @values dont_allow | allow_adult | allow_all
   */
  person_generation?: 'dont_allow' | 'allow_adult' | 'allow_all';
  /**
   * 安全过滤级别
   * @default "BLOCK_MEDIUM_AND_ABOVE"
   * @values BLOCK_NONE | BLOCK_FEW | BLOCK_SOME | BLOCK_MEDIUM_AND_ABOVE | BLOCK_MOST
   */
  safety_filter_level?: 'BLOCK_NONE' | 'BLOCK_FEW' | 'BLOCK_SOME' | 'BLOCK_MEDIUM_AND_ABOVE' | 'BLOCK_MOST';
  /**
   * 生成图像数量
   * @default 1
   * @range 1 - 4
   */
  number_of_images?: number;
  /**
   * 输出MIME类型
   * @default "image/png"
   * @values image/png | image/jpeg
   */
  output_mime_type?: 'image/png' | 'image/jpeg';
  /**
   * 提示词语言
   * @default "auto"
   */
  language?: string;
}

export interface GoogleImagen4UltraGenerateResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_imagen_4_ultra_generate
 *
 * Google Imagen 4.0 Ultra 图像生成实现。
 * 
 * Model: imagen-4.0-ultra-generate-001
 * API文档: https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/imagen-api
 * Features: 超高质量文本生成图像，支持高级功能
 * Aspect Ratios: 1:1, 9:16, 16:9, 3:4, 4:3
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleImagen4UltraGenerate } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleImagen4UltraGenerate({
 *   // 参数见 GoogleImagen4UltraGenerateParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleImagen4UltraGenerateParams 接口定义
 * @returns 生成的资源列表
 */
export async function googleImagen4UltraGenerate(
  params: GoogleImagen4UltraGenerateParams,
): Promise<GoogleImagen4UltraGenerateResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_imagen_4_ultra_generate',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleImagen4UltraGenerateResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
